import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const api      = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const MODULES = [
  {
    id:       "inventory",
    name:     "Inventory Pro",
    price:    799,
    icon:     "📦",
    color:    "#2563eb",
    bg:       "#eff6ff",
    desc:     "Multi-platform inventory sync, warehouse management, barcode scanner, AI reorder suggestions",
    features: ["Real-time stock sync across all platforms","Warehouse management with barcode scanner","AI-powered reorder suggestions","Stock transfer between locations","Low stock alerts"],
    pages:    ["inventory","wms"],
  },
  {
    id:       "finance",
    name:     "Finance & GST",
    price:    599,
    icon:     "💰",
    color:    "#15803d",
    bg:       "#f0fdf4",
    desc:     "GST invoices, GSTR-1/3B reports, expense tracking, Tally/Zoho export, P&L",
    features: ["Auto GST invoice generation","GSTR-1 and GSTR-3B reports","Expense tracking with bank import","Tally & Zoho export","Profit & Loss statements","Reconciliation"],
    pages:    ["gst","expenses","accounting","reconciliation","reports"],
  },
  {
    id:       "orders",
    name:     "Orders & Logistics",
    price:    499,
    icon:     "🚚",
    color:    "#c45000",
    bg:       "#fff3e0",
    desc:     "All platform orders, shipping labels, courier rate comparison, COD management",
    features: ["Orders from all platforms in one view","One-click shipping label print","Courier rate comparison","COD management & reconciliation","Shipment tracking","Return label generation"],
    pages:    ["orders","logistics","labels","invoicepdf"],
  },
  {
    id:       "catalog",
    name:     "Catalog & PIM",
    price:    399,
    icon:     "🗂️",
    color:    "#7e22ce",
    bg:       "#fdf4ff",
    desc:     "Product catalog, AI content generator, image formatter, SKU mapping",
    features: ["Central product catalog","AI-powered listing content generator","Platform-specific image formatter","SKU mapping across platforms","Bulk product updates"],
    pages:    ["pim","skumap","myntraImages"],
  },
  {
    id:       "customers",
    name:     "Customer Management",
    price:    499,
    icon:     "👥",
    color:    "#0891b2",
    bg:       "#ecfeff",
    desc:     "Customer profiles, order history, returns management, communication logs",
    features: ["360° customer profiles","Full order history per customer","Returns & refund management","Customer communication logs","RFM analysis"],
    pages:    ["customers","returns"],
  },
  {
    id:       "tasks",
    name:     "Task & Operations",
    price:    299,
    icon:     "✅",
    color:    "#b45309",
    bg:       "#fffbeb",
    desc:     "Task planner, team management, automation rules, department workflows",
    features: ["Task & deadline management","Department-wise team planner","Automation rules engine","WhatsApp alerts & daily digest","Role-based team access"],
    pages:    ["tasks","automation","team","whatsapp"],
  },
];

const BUNDLE_PRICE    = 2499;
const BUNDLE_FULL     = MODULES.reduce((a, m) => a + m.price, 0);
const BUNDLE_SAVINGS  = BUNDLE_FULL - BUNDLE_PRICE;

const S = {
  card:   { background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", overflow:"hidden" },
  btn:    (c="#F47920") => ({ padding:"9px 18px", borderRadius:8, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:700 }),
  btnOut: (c="#374151") => ({ padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", background:"#fff", color:c, border:"1px solid #d1d5db", fontFamily:"inherit", fontWeight:600 }),
};

export default function PageModules({ toast, tenant, onModulesUpdate }) {
  const [modules,      setModules]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activating,   setActivating]   = useState(null);
  const [trialActive,  setTrialActive]  = useState(false);
  const [trialEndsAt,  setTrialEndsAt]  = useState(null);
  const [isBundle,     setIsBundle]     = useState(false);
  const [activeIds,    setActiveIds]    = useState([]);
  const [selected,     setSelected]     = useState(null); // selected module for detail view

  const loadModules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/modules");
      setModules(data.modules || []);
      setTrialActive(data.trialActive || false);
      setTrialEndsAt(data.trialEndsAt);
      setIsBundle(data.isBundle || false);
      setActiveIds(data.activeModules || []);
      if (onModulesUpdate) onModulesUpdate(data.activeModules || []);
    } catch(e) { toast(e.message, "error"); }
    setLoading(false);
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const activateFree = async (moduleId) => {
    setActivating(moduleId);
    try {
      await api("/modules/activate", { method:"POST", body:JSON.stringify({ moduleId }) });
      toast("✅ Module activated!", "success");
      loadModules();
    } catch(e) { toast(e.message, "error"); }
    setActivating(null);
  };

  const startPayment = async (moduleId, isBundle = false) => {
    setActivating(moduleId || "bundle");
    try {
      const order = await api("/modules/create-order", { method:"POST", body:JSON.stringify({ moduleId, isBundle }) });
      const mod   = MODULES.find(m => m.id === moduleId);

      const options = {
        key:         order.keyId,
        amount:      order.amount,
        currency:    "INR",
        name:        "3June",
        description: isBundle ? "All Modules Bundle" : mod?.name + " Module",
        order_id:    order.orderId,
        theme:       { color: "#F47920" },
        prefill: {
          email: tenant?.email || "",
          name:  tenant?.storeName || "",
        },
        handler: async (response) => {
          try {
            const endpoint = isBundle ? "/modules/activate-bundle" : "/modules/activate";
            await api(endpoint, {
              method: "POST",
              body:   JSON.stringify({
                moduleId,
                razorpayPaymentId:  response.razorpay_payment_id,
                razorpayOrderId:    response.razorpay_order_id,
                razorpaySignature:  response.razorpay_signature,
              }),
            });
            toast("✅ " + (isBundle ? "All modules" : mod?.name) + " activated!", "success");
            loadModules();
          } catch(e) { toast(e.message, "error"); }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch(e) { toast(e.message, "error"); }
    setActivating(null);
  };

  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - Date.now()) / 86400000))
    : 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:18 }}>🧩 Modules</div>
          <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>Activate only what you need. Add more anytime.</div>
        </div>
        {trialActive && (
          <div style={{ background:"#fff8f2", border:"1px solid #fed7aa", borderRadius:8, padding:"8px 14px", fontSize:12 }}>
            <span style={{ fontWeight:700, color:"#F47920" }}>🎁 Free Trial</span>
            <span style={{ color:"#92400e", marginLeft:6 }}>{trialDaysLeft} days left — all modules active</span>
          </div>
        )}
      </div>

      {/* Bundle offer */}
      {!isBundle && (
        <div style={{ background:"linear-gradient(135deg,#1A3A5C,#2563eb)", borderRadius:12, padding:"18px 24px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:16, marginBottom:4 }}>⚡ All Modules Bundle — Save ₹{BUNDLE_SAVINGS.toLocaleString("en-IN")}/month</div>
            <div style={{ color:"rgba(255,255,255,0.8)", fontSize:12 }}>
              Get all 6 modules for ₹{BUNDLE_PRICE.toLocaleString("en-IN")}/mo instead of ₹{BUNDLE_FULL.toLocaleString("en-IN")}/mo
            </div>
            <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
              {MODULES.map(m => (
                <span key={m.id} style={{ background:"rgba(255,255,255,0.15)", color:"#fff", fontSize:10, padding:"2px 8px", borderRadius:10 }}>{m.icon} {m.name}</span>
              ))}
            </div>
          </div>
          <div style={{ textAlign:"center", flexShrink:0 }}>
            <div style={{ color:"rgba(255,255,255,0.6)", fontSize:11, textDecoration:"line-through", marginBottom:2 }}>₹{BUNDLE_FULL.toLocaleString("en-IN")}/mo</div>
            <div style={{ color:"#F47920", fontWeight:800, fontSize:26 }}>₹{BUNDLE_PRICE.toLocaleString("en-IN")}</div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:10, marginBottom:10 }}>per month + GST</div>
            <button style={{ ...S.btn("#F47920"), fontSize:13, padding:"10px 24px" }} onClick={() => startPayment(null, true)}>
              Get All Modules →
            </button>
          </div>
        </div>
      )}

      {isBundle && (
        <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 18px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>✅</span>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:"#15803d" }}>All Modules Bundle Active</div>
            <div style={{ fontSize:12, color:"#166534" }}>You have access to all current and future modules</div>
          </div>
        </div>
      )}

      {/* Module grid */}
      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading modules…</div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
          {MODULES.map((mod) => {
            const serverMod  = modules.find(m => m.id === mod.id) || {};
            const hasAccess  = serverMod.hasAccess || isBundle || trialActive;
            const isActive   = activeIds.includes(mod.id) || isBundle;
            const isLoading  = activating === mod.id;
            const expiresAt  = serverMod.activeUntil;
            const daysLeft   = expiresAt ? Math.max(0, Math.ceil((new Date(expiresAt)-Date.now())/86400000)) : null;

            return (
              <div key={mod.id} style={{ ...S.card, border:isActive?"2px solid "+mod.color:"1px solid #e5e7eb" }}>
                {/* Header */}
                <div style={{ background:isActive?mod.color:mod.bg, padding:"14px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:24 }}>{mod.icon}</span>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13, color:isActive?"#fff":mod.color }}>{mod.name}</div>
                      <div style={{ fontSize:10, color:isActive?"rgba(255,255,255,0.8)":mod.color, opacity:0.8 }}>
                        ₹{mod.price.toLocaleString("en-IN")}/month + GST
                      </div>
                    </div>
                  </div>
                  {isActive && (
                    <span style={{ background:"rgba(255,255,255,0.25)", color:"#fff", fontSize:9, padding:"2px 8px", borderRadius:10, fontWeight:700 }}>
                      {trialActive?"TRIAL":"ACTIVE"}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div style={{ padding:"14px 16px" }}>
                  <div style={{ fontSize:11, color:"#6b7280", marginBottom:10, lineHeight:1.5 }}>{mod.desc}</div>

                  <div style={{ marginBottom:12 }}>
                    {mod.features.map((f, i) => (
                      <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:6, marginBottom:4, fontSize:11 }}>
                        <span style={{ color:mod.color, flexShrink:0, marginTop:1 }}>✓</span>
                        <span style={{ color:"#374151" }}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {daysLeft !== null && !trialActive && (
                    <div style={{ fontSize:10, color:daysLeft<7?"#dc2626":"#6b7280", marginBottom:8 }}>
                      {daysLeft > 0 ? `Expires in ${daysLeft} days` : "Expired — renew to continue"}
                    </div>
                  )}

                  {/* Action button */}
                  {isBundle ? (
                    <div style={{ fontSize:11, color:"#15803d", fontWeight:600 }}>✅ Included in your bundle</div>
                  ) : isActive && !trialActive ? (
                    <button style={{ ...S.btn(mod.color), width:"100%", fontSize:11 }} onClick={() => startPayment(mod.id)} disabled={isLoading}>
                      {isLoading?"Processing…":"🔄 Renew — ₹"+mod.price.toLocaleString("en-IN")+"/mo"}
                    </button>
                  ) : !isActive && !trialActive ? (
                    <button style={{ ...S.btn(mod.color), width:"100%", fontSize:11 }} onClick={() => startPayment(mod.id)} disabled={isLoading}>
                      {isLoading?"Processing…":"+ Activate — ₹"+mod.price.toLocaleString("en-IN")+"/mo"}
                    </button>
                  ) : (
                    <div style={{ fontSize:11, color:mod.color, fontWeight:600 }}>
                      🎁 Active on free trial ({trialDaysLeft} days left)
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Core module info */}
      <div style={{ marginTop:16, background:"#f9fafb", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>🏠</span>
          <div>
            <div style={{ fontWeight:700, fontSize:13 }}>Core — Always Free</div>
            <div style={{ fontSize:11, color:"#6b7280" }}>Dashboard, basic inventory (1 platform), account management — included for all users, forever</div>
          </div>
          <span style={{ marginLeft:"auto", background:"#f0fdf4", color:"#15803d", fontSize:10, padding:"2px 8px", borderRadius:10, fontWeight:700, flexShrink:0 }}>FREE</span>
        </div>
      </div>

      {/* Razorpay script */}
      {!window.Razorpay && (
        <script src="https://checkout.razorpay.com/v1/checkout.js" async/>
      )}
    </div>
  );
}
